import type { AnalyticsPlugin } from 'analytics';
import { EVENTS } from '../constants/events';

import clarity from '@microsoft/clarity';

interface ClarityConfig {
  projectId: string;
  options?: {
    debug?: boolean;
    upload?: string;
    track?: boolean;
    lean?: boolean;
    content?: boolean;
    doNotTrack?: boolean;
    disableTelemetry?: boolean;
    disableAutoCapture?: boolean;
  };
}

export default class ClarityPlugin implements AnalyticsPlugin {
  [x: string]: unknown; // Required by AnalyticsPlugin interface
  public name = 'microsoft-clarity';
  public config: ClarityConfig;

  constructor(
    config: ClarityConfig = {
      projectId: '',
      options: { debug: false, track: true },
    }
  ) {
    this.config = config;
  }

  initialize({ config }: { config: ClarityConfig }) {
    const {
      projectId,
      options: { debug },
    } = config;
    if (!projectId) return;
    
    // O Clarity já é inicializado pelo script no index.html
    // Este método apenas verifica se está carregado corretamente
    if (typeof window !== 'undefined' && 'clarity' in window) {
      console.log('[Clarity] ✅ Inicializado via script HTML');
      
      // Log de debug para verificar captura de CSS
      if (debug) {
        setTimeout(() => {
          const styleSheets = document.styleSheets;
          console.log(`[Clarity] CSS Stylesheets detectadas: ${styleSheets.length}`);
        }, 1000);
      }
    } else {
      console.warn('[Clarity] ⚠️ Script não carregado. Verifique o VITE_CLARITY_PROJECT_ID');
    }
  }

  page() {
    clarity.event('page_view');
  }

  track({ payload }: { payload: { event: string; properties?: Record<string, unknown> } }) {
    const { event, properties } = payload;

    if (properties) {
      Object.entries(properties).forEach(([key, value]) => {
        if (typeof value === 'string' || Array.isArray(value)) {
          clarity.setTag(key, value);
        }
      });
    }

    switch (event) {
      case EVENTS.PAGE_VIEW:
        clarity.event('page_view');
        break;
      case EVENTS.VIEW_CONTENT:
        clarity.event('view_content');
        break;
      case EVENTS.ADD_TO_CART:
        clarity.event('add_to_cart');
        break;
      case EVENTS.INITIATE_CHECKOUT:
        clarity.event('initiate_checkout');
        break;
      case EVENTS.ADD_PAYMENT_INFO:
        clarity.event('add_payment_info');
        break;
      case EVENTS.PURCHASE:
        clarity.event('purchase');
        break;

      // Eventos de status de pedido
      case EVENTS.ORDER_CREATED:
        clarity.event('order_created');
        break;
      case EVENTS.ORDER_ACCEPTED:
        clarity.event('order_accepted');
        break;
      case EVENTS.ORDER_OUT_FOR_DELIVERY:
        clarity.event('order_out_for_delivery');
        break;
      case EVENTS.ORDER_READY:
        clarity.event('order_ready');
        break;
      case EVENTS.ORDER_DELIVERED:
        clarity.event('order_delivered');
        break;
      case EVENTS.ORDER_CANCELED:
        clarity.event('order_canceled');
        break;

      default:
        clarity.event(event);
    }
  }

  identify({ userId }: { userId: string }) {
    clarity.identify(userId);
  }

  loaded() {
    return typeof window !== 'undefined';
  }

  ready() {
    return typeof window !== 'undefined';
  }
}
